import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { api } from '../services/api';
import { loggerUtils } from '../services/logger';
import { apiClient } from '../lib/api';

// Check if we're on the client side
const isClient = typeof window !== 'undefined';

export interface User {
  id: string;
  email: string;
  is_admin: boolean;
  avatar_url?: string;
}

export interface Project {
  id: number;
  name: string;
  description: string | null;
  created_at: string;
  updated_at: string;
}

export interface Bucket {
  id: number;
  name: string;
  description: string | null;
  project_id: number;
  created_at: string;
}

export interface Folder {
  id: number;
  name: string;
  bucket_id: number;
  parent_folder_id?: number | null;
  created_at: string;
  updated_at: string;
}

interface AppState {
  user: User | null;
  projects: Project[];
  buckets: Bucket[];
  folders: Folder[];
  loading: boolean;
  initialized: boolean;
  error: string;
  loadingProjects: boolean;
  loadingBuckets: boolean;
  loadingFolders: boolean;
  projectsLoadedAt?: number;
  bucketsLoadedAt?: number;
  foldersLoadedAt?: number;
  projectsPromise?: Promise<void>;
  bucketsPromise?: Promise<void>;
  foldersPromise?: Promise<void>;
}

interface AppActions {
  setUser: (user: User | null) => void;
  setProjects: (projects: Project[]) => void;
  setBuckets: (buckets: Bucket[]) => void;
  setFolders: (folders: Folder[]) => void;
  addProject: (project: Project) => void;
  addBucket: (bucket: Bucket) => void;
  addFolder: (folder: Folder) => void;
  removeProject: (projectId: number) => void;
  removeBucket: (bucketId: number) => void;
  removeFolder: (folderId: number) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string) => void;
  clearError: () => void;
  logout: () => void;
  refreshUser: () => Promise<void>;
  refreshProjects: (force?: boolean) => Promise<void>;
  refreshBuckets: (projectId?: number, force?: boolean) => Promise<void>;
  refreshFolders: (bucketId?: number, force?: boolean) => Promise<void>;
  initialize: () => Promise<void>;
  createProject: (name: string,   description: string | null) => Promise<Project>;
  createBucket: (projectId: number, name: string,   description: string | null) => Promise<Bucket>;
  createFolder: (bucketId: number, name: string, parentFolderId?: number) => Promise<Folder>;
  deleteProject: (projectId: number) => Promise<void>;
  deleteBucket: (bucketId: number) => Promise<void>;
  deleteFolder: (folderId: number) => Promise<void>;
  uploadAvatar: (file: File) => Promise<void>;
  deleteAvatar: () => Promise<void>;
  updateUserProfile: (data: { name?: string; email?: string }) => Promise<void>;
  isAuthenticated: () => boolean;
}

type AppStore = AppState & AppActions;

// No token refresh management needed - backend middleware handles it automatically!

const store = create<AppStore>()(
  devtools(
    (set, get) => ({
      // Initial state - loading false until initialize is called
      user: null,
      projects: [],
      buckets: [],
      folders: [],
      loading: false,
      initialized: false,
      error: '',
      loadingProjects: false,
      loadingBuckets: false,
      loadingFolders: false,

       // Actions
      setUser: (user) => set({ user }, false, 'setUser'),
      setProjects: (projects) => set({ projects }, false, 'setProjects'),
      setBuckets: (buckets) => set({ buckets }, false, 'setBuckets'),
      setFolders: (folders) => set({ folders }, false, 'setFolders'),
      
      addProject: (project) => set(
        (state) => ({ projects: [...state.projects, project] }),
        false,
        'addProject'
      ),
      
      addBucket: (bucket) => set(
        (state) => ({ buckets: [...state.buckets, bucket] }),
        false,
        'addBucket'
      ),

      addFolder: (folder) => set(
        (state) => ({ folders: [...state.folders, folder] }),
        false,
        'addFolder'
      ),

      removeProject: (projectId) => set(
        (state) => ({ projects: state.projects.filter(p => p.id !== projectId) }),
        false,
        'removeProject'
      ),
      
      removeBucket: (bucketId) => set(
        (state) => ({ buckets: state.buckets.filter(b => b.id !== bucketId) }),
        false,
        'removeBucket'
      ),

      removeFolder: (folderId) => set(
        (state) => ({ folders: state.folders.filter(f => f.id !== folderId) }),
        false,
        'removeFolder'
      ),
      
      setLoading: (loading) => set({ loading }, false, 'setLoading'),
      
      setError: (error) => set({ error }, false, 'setError'),
      
      clearError: () => set({ error: '' }, false, 'clearError'),
      
      logout: async () => {
        try {
          await api.logout();
        } catch (_error) {
          loggerUtils.auth('logout', { error: _error instanceof Error ? _error.message : String(_error), success: false });
        }
        set({ user: null, buckets: [], projects: [], error: '' }, false, 'logout');
      },

      refreshUser: async () => {
        if (!isClient) return;
        
        try {
          loggerUtils.auth('token_refresh', { action: 'session_check' });
          
          // Check session and extract both user info and CSRF token
          const sessionData = await api.checkSession() as { 
            authenticated: boolean; 
            user: { id: string; email: string; is_admin: boolean; avatar_url?: string };
            csrf_token?: string;
          };
          
          if (sessionData.authenticated && sessionData.user) {
            // Extract CSRF token from session if available (for session recovery)
            if (sessionData.csrf_token) {
              // Store CSRF token in API service for subsequent requests
              (api as any).csrfToken = sessionData.csrf_token;
            }
            
            set({ user: sessionData.user, error: '' }, false, 'refreshUser:success');
            loggerUtils.auth('token_refresh', { success: true });
          } else {
            // Session not authenticated - this is normal, not an error
            // Clear user state but don't trigger logout events
            set({ user: null, error: '', projects: [], buckets: [] }, false, 'refreshUser:notAuthenticated');
            loggerUtils.auth('token_refresh', { success: false, reason: 'not_authenticated' });
          }
          
        } catch (error) {
          loggerUtils.auth('auth_error', { error: error instanceof Error ? error.message : String(error) });
          
          // Only emit logout events for actual network/server errors, not auth failures
          // Auth failures are handled above in the normal response flow
          const isNetworkError = error instanceof Error && 
            (error.message.includes('fetch') || error.message.includes('network') || error.message.includes('timeout'));
            
          if (isNetworkError) {
            // Network error - keep current user state, show error message
            set({ error: 'Network error checking session' }, false, 'refreshUser:networkError');
          } else {
            // Other error - clear user state but don't trigger logout cascade
            set({ user: null, error: '', projects: [], buckets: [] }, false, 'refreshUser:error');
          }
        }
      },

      refreshProjects: async (force = false): Promise<void> => {
        if (!isClient) return Promise.resolve();
        const { loadingProjects, projectsLoadedAt, projectsPromise } = get();
        // Note: We no longer check user here since it's managed by React Query
        // The AuthProvider ensures we only call this when authenticated

        // If already loading, wait for the existing promise
        if (loadingProjects && projectsPromise) {
          return projectsPromise;
        }

        if (!force && projectsLoadedAt && Date.now() - projectsLoadedAt < 10000) {
          return Promise.resolve();
        }

        // Create and store the promise
        const promise = (async () => {
          set({ loadingProjects: true }, false, 'refreshProjects:start');
          try {
            const projectsData = await api.getProjects();
            set({ projects: projectsData, error: '', projectsLoadedAt: Date.now() }, false, 'refreshProjects:success');
          } catch (_error) {
            const errorMsg = 'Network error loading projects';
            set({ error: errorMsg }, false, 'refreshProjects:networkError');
          } finally {
            set({ loadingProjects: false, projectsPromise: undefined }, false, 'refreshProjects:complete');
          }
        })();

        set({ projectsPromise: promise }, false, 'refreshProjects:promiseStored');
        return promise;
      },

      refreshBuckets: async (projectId?: number, force = false) => {
        if (!isClient) return;
        const { loadingBuckets, bucketsLoadedAt } = get();
        // Note: We no longer check user here since it's managed by React Query
        // The AuthProvider ensures we only call this when authenticated
        if (loadingBuckets) return;
        if (!force && bucketsLoadedAt && Date.now() - bucketsLoadedAt < 10000) return;
        set({ loadingBuckets: true }, false, 'refreshBuckets:start');
        try {
          const bucketsData = projectId 
            ? await api.getBucketsForProject(projectId)
            : await api.getBuckets();
          set({ buckets: bucketsData, error: '', bucketsLoadedAt: Date.now() }, false, 'refreshBuckets:success');
        } catch (_error) {
          const err = _error as { status?: number } | Error | unknown;
          if (typeof err === 'object' && err !== null && 'status' in err && (err as any).status === 401) {
            set({ user: null, buckets: [], error: 'Session expired. Please log in again.' }, false, 'refreshBuckets:unauthorized');
          } else {
            const errorMsg = 'Network error loading buckets';
            set({ error: errorMsg }, false, 'refreshBuckets:networkError');
          }
        } finally {
          set({ loadingBuckets: false }, false, 'refreshBuckets:complete');
        }
      },

      refreshFolders: async (bucketId?: number, force = false) => {
        if (!isClient) return;
        const { loadingFolders, foldersLoadedAt } = get();
        // Note: We no longer check user here since it's managed by React Query
        // The AuthProvider ensures we only call this when authenticated
        if (loadingFolders) return;
        if (!force && foldersLoadedAt && Date.now() - foldersLoadedAt < 10000) return;
        set({ loadingFolders: true }, false, 'refreshFolders:start');
        try {
          // For now, return empty folders since backend doesn't have folders API yet
          set({ folders: [], error: '', foldersLoadedAt: Date.now() }, false, 'refreshFolders:success');
        } catch (_error) {
          const errorMsg = 'Network error loading folders';
          set({ error: errorMsg }, false, 'refreshFolders:networkError');
        } finally {
          set({ loadingFolders: false }, false, 'refreshFolders:complete');
        }
      },

      initialize: async () => {
        const currentState = get();

        // Skip if already initialized or currently initializing
        if (currentState.initialized || currentState.loading) {
          return;
        }

        set({ loading: true, error: '' }, false, 'initialize:start');

        // Set up automatic CSRF token refresh callback
        if (isClient && apiClient.setOnCsrfTokenUpdate) {
          apiClient.setOnCsrfTokenUpdate((newToken: string) => {
            loggerUtils.auth('csrf_token_refreshed', { tokenLength: newToken.length });
            // Token is already updated in API client, no need to update store
            // as the auth store manages its own CSRF token separately
          });
        }

        // Set up auth event listeners
        if (isClient) {
          // Handle logout events
          const handleLogout = () => {
            loggerUtils.auth('forced_logout', { reason: 'auth_event' });
            // Clear state on logout
            set({ user: null, error: '', projects: [], buckets: [] }, false, 'forced_logout');
          };

          // Handle login events
          const handleLogin = () => {
            loggerUtils.auth('user_login', { reason: 'auth_event' });
            // No token refresh needed - backend middleware handles it automatically
          };

          window.addEventListener('auth:logout', handleLogout);
          window.addEventListener('auth:login', handleLogin);
          
          
          // Clean up on unmount (though store persists)
          if (typeof window !== 'undefined') {
            window.addEventListener('beforeunload', () => {
              window.removeEventListener('auth:logout', handleLogout);
              window.removeEventListener('auth:login', handleLogin);
            });
          }
        }
        
        try {
          await get().refreshUser();
          if (get().isAuthenticated()) {
            await get().refreshProjects(true);
            await get().refreshBuckets(undefined, true);

            // No token refresh needed - backend middleware handles it automatically
          }
        } catch (_error) {
          // errors already handled in sub-calls
        } finally {
          set({ loading: false, initialized: true }, false, 'initialize:complete');
        }
      },

      createProject: async (name: string,   description: string | null) => {
        const { user } = get();
        if (!user) {
          const error = 'Not authenticated';
          set({ error }, false, 'createProject:noAuth');
          throw new Error(error);
        }

        try {
          const newProject = await api.createProject(name.trim(), description?.trim());
          get().addProject(newProject);
          set({ error: '' }, false, 'createProject:success');
          return newProject;
        } catch (_error) {
          if (_error instanceof Error) {
            if (_error.message.includes('Session expired')) {
              set({ user: null, projects: [], error: _error.message }, false, 'createProject:unauthorized');
            } else {
              set({ error: _error.message }, false, 'createProject:error');
            }
            throw _error;
          }
          const unknownError = 'Unknown error creating project';
          set({ error: unknownError }, false, 'createProject:unknownError');
          throw new Error(unknownError);
        }
      },

      createBucket: async (projectId: number, name: string,   description: string | null) => {
        const { user } = get();
        if (!user) {
          const error = 'Not authenticated';
          set({ error }, false, 'createBucket:noAuth');
          throw new Error(error);
        }

        try {
          const newBucket = await api.createBucket(name.trim(), description?.trim(), projectId);
          get().addBucket(newBucket);
          set({ error: '' }, false, 'createBucket:success');
          return newBucket;
        } catch (_error) {
          if (_error instanceof Error) {
            if (_error.message.includes('Session expired')) {
              set({ user: null, buckets: [], error: _error.message }, false, 'createBucket:unauthorized');
            } else {
              set({ error: _error.message }, false, 'createBucket:error');
            }
            throw _error;
          }
          const unknownError = 'Unknown error creating bucket';
          set({ error: unknownError }, false, 'createBucket:unknownError');
          throw new Error(unknownError);
        }
      },

      deleteBucket: async (bucketId: number) => {
        const { user } = get();
        if (!user) {
          const error = 'Not authenticated';
          set({ error }, false, 'deleteBucket:noAuth');
          throw new Error(error);
        }

        try {
          await api.deleteBucket(bucketId);
          get().removeBucket(bucketId);
          set({ error: '' }, false, 'deleteBucket:success');
        } catch (_error) {
          if (_error instanceof Error) {
            if (_error.message.includes('Session expired')) {
              set({ user: null, buckets: [], error: _error.message }, false, 'deleteBucket:unauthorized');
            } else {
              set({ error: _error.message }, false, 'deleteBucket:error');
            }
            throw _error;
          }
          const unknownError = 'Unknown error deleting bucket';
          set({ error: unknownError }, false, 'deleteBucket:unknownError');
          throw new Error(unknownError);
        }
      },

      createFolder: async (bucketId: number, name: string, parentFolderId?: number) => {
        // For now, simulate creating a folder since backend doesn't support it yet
        const newFolder = {
          id: Date.now(),
          name: name.trim(),
          bucket_id: bucketId,
          parent_folder_id: parentFolderId,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        get().addFolder(newFolder);
        return newFolder;
      },

      deleteProject: async (projectId: number) => {
        const { user } = get();
        if (!user) {
          const error = 'Not authenticated';
          set({ error }, false, 'deleteProject:noAuth');
          throw new Error(error);
        }

        try {
          await api.deleteProject(projectId);
          get().removeProject(projectId);
          set({ error: '' }, false, 'deleteProject:success');
        } catch (_error) {
          if (_error instanceof Error) {
            if (_error.message.includes('Session expired')) {
              set({ user: null, projects: [], error: _error.message }, false, 'deleteProject:unauthorized');
            } else {
              set({ error: _error.message }, false, 'deleteProject:error');
            }
            throw _error;
          }
          const unknownError = 'Unknown error deleting project';
          set({ error: unknownError }, false, 'deleteProject:unknownError');
          throw new Error(unknownError);
        }
      },

      deleteFolder: async (folderId: number) => {
        // For now, simulate deleting a folder since backend doesn't support it yet
        get().removeFolder(folderId);
      },

      uploadAvatar: async (file: File) => {
        try {
          const response = await api.uploadAvatar(file);
          const currentUser = get().user;
          if (currentUser) {
            set({
              user: { ...currentUser, avatar_url: response.avatar_url }
            }, false, 'uploadAvatar:success');
          }
        } catch (error) {
          console.error('Failed to upload avatar:', error);
          throw error;
        }
      },

      deleteAvatar: async () => {
        try {
          await api.deleteAvatar();
          const currentUser = get().user;
          if (currentUser) {
            set({
              user: { ...currentUser, avatar_url: undefined }
            }, false, 'deleteAvatar:success');
          }
        } catch (error) {
          console.error('Failed to delete avatar:', error);
          throw error;
        }
      },

      updateUserProfile: async (_data: { name?: string; email?: string }) => {
        // Profile updates not implemented in backend yet
        throw new Error('Profile updates not implemented yet');
      },

      isAuthenticated: () => {
        if (!isClient) return false;
        const { user } = get();
        return !!user;
      },
    }),
    {
      name: 'app-store',
    }
  )
);

// Export the store for direct access
export const appStore = store;

// Default state for SSR/hydration
const _defaultState = {
  user: null,
  projects: [],
  buckets: [],
  folders: [],
  loading: false,
  initialized: false,
  error: '',
  loadingProjects: false,
  loadingBuckets: false,
  loadingFolders: false,
  setUser: () => {},
  setProjects: () => {},
  setBuckets: () => {},
  setFolders: () => {},
  addProject: () => {},
  addBucket: () => {},
  addFolder: () => {},
  removeProject: () => {},
  removeBucket: () => {},
  removeFolder: () => {},
  setLoading: () => {},
  setError: () => {},
  clearError: () => {},
  logout: () => {},
  refreshUser: async () => {},
  refreshProjects: async () => {},
  refreshBuckets: async () => {},
  refreshFolders: async () => {},
  initialize: async () => {},
  createProject: async () => ({} as Record<string, unknown>),
  createBucket: async () => ({} as Record<string, unknown>),
  createFolder: async () => ({} as Record<string, unknown>),
  deleteProject: async () => {},
  deleteBucket: async () => {},
  deleteFolder: async () => {},
  uploadAvatar: async () => {},
  deleteAvatar: async () => {},
  updateUserProfile: async () => {},
  isAuthenticated: () => false,
} as const;

// Client-safe hook that returns default values during SSR
export const useAppStore = Object.assign(((...args: Parameters<typeof store>) => store(...args)) as typeof store, store);
